import { hoyLocal, fechaLocal } from '@/lib/fechas';
import { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import api, { formatoMXN, formatoFecha, ESTADOS } from '@/lib/api';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Badge } from '@/components/ui/badge';
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from '@/components/ui/table';
import { Skeleton } from '@/components/ui/skeleton';
import {
  DollarSign, TrendingUp, ClipboardList, CheckCircle,
  AlertCircle, Car, ArrowRight, ArrowUpRight, ArrowDownRight, Users
} from 'lucide-react';
import { toast } from 'sonner';
import { BarChart, Bar, XAxis, YAxis, Tooltip, ResponsiveContainer, Cell } from 'recharts';
import ComparativaMensual from '@/componentes/ComparativaMensual';

const coloresEstado = {
  recibido: '#94a3b8', diagnostico: '#60a5fa', reparando: '#fbbf24',
  terminado: '#34d399', entregado: '#4ade80', cancelado: '#f87171'
};

function getWeekRange() {
  const hoy = new Date();
  const lunes = new Date(hoy);
  lunes.setDate(hoy.getDate() - hoy.getDay() + (hoy.getDay() === 0 ? -6 : 1));
  const domingo = new Date(lunes);
  domingo.setDate(lunes.getDate() + 6);
  return { desde: fechaLocal(lunes), hasta: fechaLocal(domingo) };
}

export default function Dashboard() {
  const [metricas, setMetricas] = useState(null);
  const [resumenSemanal, setResumenSemanal] = useState(null);
  const [cargando, setCargando] = useState(true);
  const navigate = useNavigate();

  useEffect(() => {
    const cargar = async () => {
      try {
        const semana = getWeekRange();
        const [metRes, resRes] = await Promise.all([
          api.get('/dashboard/metricas'),
          api.get('/contabilidad/resumen', { params: semana })
        ]);
        setMetricas(metRes.data);
        setResumenSemanal(resRes.data);
      } catch {
        toast.error('Error al cargar metricas del dashboard');
      } finally {
        setCargando(false);
      }
    };
    cargar();
  }, []);

  if (cargando) {
    return (
      <div className="space-y-6" data-testid="dashboard-loading">
        <Skeleton className="h-10 w-48 bg-slate-800" />
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-4">
          {[...Array(4)].map((_, i) => <Skeleton key={i} className="h-32 bg-slate-800" />)}
        </div>
      </div>
    );
  }

  if (!metricas) return null;

  const chartData = Object.entries(metricas.distribucion_estados || {}).map(([key, val]) => ({
    nombre: ESTADOS[key]?.label || key, valor: val, fill: coloresEstado[key]
  }));

  const tarjetas = [
    { titulo: 'Ingresos Hoy', valor: formatoMXN(metricas.ingresos_hoy), icono: DollarSign, color: 'text-amber-500', bg: 'bg-amber-500/10' },
    { titulo: 'Ingresos del Mes', valor: formatoMXN(metricas.ingresos_mes), icono: TrendingUp, color: 'text-emerald-400', bg: 'bg-emerald-500/10', variacion: metricas.variacion_pct },
    { titulo: 'Órdenes Abiertas', valor: metricas.ordenes_abiertas, icono: ClipboardList, color: 'text-blue-400', bg: 'bg-blue-500/10' },
    { titulo: 'Órdenes Completadas', valor: metricas.ordenes_completadas, icono: CheckCircle, color: 'text-green-400', bg: 'bg-green-500/10' },
    { titulo: 'Pagos Pendientes', valor: formatoMXN(metricas.total_pendiente), icono: AlertCircle, color: 'text-orange-400', bg: 'bg-orange-500/10' },
    { titulo: 'Vehículos en Servicio', valor: metricas.vehiculos_en_servicio, icono: Car, color: 'text-violet-400', bg: 'bg-violet-500/10' },
  ];

  const semanaCards = resumenSemanal ? [
    { titulo: 'Ingresos Semana', valor: formatoMXN(resumenSemanal.ingresos), icono: ArrowUpRight, color: 'text-green-400', bg: 'bg-green-500/10', border: 'hover:border-green-800', tab: 'ingresos' },
    { titulo: 'Gastos Semana', valor: formatoMXN(resumenSemanal.gastos), icono: ArrowDownRight, color: 'text-red-400', bg: 'bg-red-500/10', border: 'hover:border-red-800', tab: 'gastos' },
    { titulo: 'Ganancia Neta', valor: formatoMXN(resumenSemanal.ganancia_neta), icono: DollarSign, color: resumenSemanal.ganancia_neta >= 0 ? 'text-emerald-400' : 'text-red-400', bg: 'bg-emerald-500/10', border: '', tab: null },
    { titulo: 'Nomina Semana', valor: formatoMXN(resumenSemanal.total_nomina), icono: Users, color: 'text-amber-500', bg: 'bg-amber-500/10', border: 'hover:border-amber-800', tab: 'nomina' },
  ] : [];

  return (
    <div className="space-y-6 animate-fadeIn" data-testid="dashboard-page">
      <h1 className="font-barlow text-3xl font-bold uppercase tracking-tight text-slate-50">
        Dashboard
      </h1>

      <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-4">
        {tarjetas.map((t, i) => {
          const Icon = t.icono;
          return (
            <Card key={i} className="bg-slate-900/40 border-slate-800 hover:border-slate-700 transition-colors"
              data-testid={`metric-card-${i}`}>
              <CardContent className="p-5">
                <div className="flex items-start justify-between">
                  <div>
                    <p className="text-xs font-medium uppercase tracking-wider text-slate-500 mb-2">{t.titulo}</p>
                    <p className="text-2xl font-bold text-slate-50 font-barlow">{t.valor}</p>
                    {t.variacion !== undefined && t.variacion !== null && (
                      <p className={`text-[11px] mt-1 font-medium ${
                        t.variacion > 0 ? 'text-emerald-400' : t.variacion < 0 ? 'text-red-400' : 'text-slate-500'
                      }`} data-testid="variacion-mes-badge">
                        {t.variacion > 0 ? '▲' : t.variacion < 0 ? '▼' : '='} {Math.abs(t.variacion)}% vs mes ant.
                      </p>
                    )}
                  </div>
                  <div className={`w-10 h-10 rounded-sm ${t.bg} flex items-center justify-center`}>
                    <Icon className={`w-5 h-5 ${t.color}`} strokeWidth={1.5} />
                  </div>
                </div>
              </CardContent>
            </Card>
          );
        })}
      </div>

      {/* Resumen Semanal */}
      {resumenSemanal && (
        <div>
          <div className="flex items-center justify-between mb-3">
            <h2 className="font-barlow text-lg font-bold uppercase tracking-tight text-slate-200">
              Resumen Semanal
            </h2>
            <button onClick={() => navigate('/contabilidad')}
              className="text-xs text-amber-500 hover:text-amber-400 flex items-center gap-1 transition-colors"
              data-testid="ver-contabilidad-btn">
              Ver contabilidad completa <ArrowRight className="w-3 h-3" />
            </button>
          </div>
          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-4">
            {semanaCards.map((t, i) => {
              const Icon = t.icono;
              const clickable = !!t.tab;
              return (
                <Card key={i}
                  className={`bg-slate-900/40 border-slate-800 transition-colors ${clickable ? `cursor-pointer ${t.border}` : ''}`}
                  onClick={clickable ? () => navigate('/contabilidad', { state: { tab: t.tab } }) : undefined}
                  data-testid={`semana-card-${t.tab || 'ganancia'}`}>
                  <CardContent className="p-5">
                    <div className="flex items-start justify-between">
                      <div>
                        <p className="text-xs font-medium uppercase tracking-wider text-slate-500 mb-2">{t.titulo}</p>
                        <p className={`text-2xl font-bold font-barlow ${t.color}`}>{t.valor}</p>
                        {clickable && <p className="text-[10px] text-slate-600 mt-1">Clic para ver desglose</p>}
                      </div>
                      <div className={`w-10 h-10 rounded-sm ${t.bg} flex items-center justify-center`}>
                        <Icon className={`w-5 h-5 ${t.color}`} strokeWidth={1.5} />
                      </div>
                    </div>
                  </CardContent>
                </Card>
              );
            })}
          </div>
        </div>
      )}

      {/* Comparativo Mensual (6/12 meses) */}
      <ComparativaMensual etiquetaOperaciones="Órdenes" />

      <div className="grid grid-cols-1 lg:grid-cols-2 gap-4">
        <Card className="bg-slate-900/40 border-slate-800">
          <CardHeader className="pb-2">
            <CardTitle className="font-barlow text-lg uppercase tracking-tight text-slate-200">
              Distribucion de Estados
            </CardTitle>
          </CardHeader>
          <CardContent>
            <ResponsiveContainer width="100%" height={220}>
              <BarChart data={chartData} margin={{ top: 5, right: 5, bottom: 5, left: 5 }}>
                <XAxis dataKey="nombre" tick={{ fill: '#64748b', fontSize: 11 }} axisLine={false} tickLine={false} />
                <YAxis tick={{ fill: '#64748b', fontSize: 11 }} axisLine={false} tickLine={false} allowDecimals={false} />
                <Tooltip
                  contentStyle={{ backgroundColor: '#1e293b', border: '1px solid #334155', borderRadius: 4, color: '#f8fafc' }}
                  cursor={{ fill: 'rgba(148,163,184,0.1)' }}
                />
                <Bar dataKey="valor" radius={[2, 2, 0, 0]}>
                  {chartData.map((entry, index) => (
                    <Cell key={index} fill={entry.fill} />
                  ))}
                </Bar>
              </BarChart>
            </ResponsiveContainer>
          </CardContent>
        </Card>

        <Card className="bg-slate-900/40 border-slate-800">
          <CardHeader className="pb-2">
            <CardTitle className="font-barlow text-lg uppercase tracking-tight text-slate-200">
              Mejores Clientes
            </CardTitle>
          </CardHeader>
          <CardContent>
            {metricas.mejores_clientes?.length > 0 ? (
              <div className="space-y-3">
                {metricas.mejores_clientes.map((c, i) => (
                  <div key={i} className="flex items-center justify-between py-2 border-b border-slate-800/50 last:border-0">
                    <div className="flex items-center gap-3">
                      <span className="text-xs text-slate-500 w-5">#{i + 1}</span>
                      <span className="text-sm text-slate-300">{c.nombre}</span>
                    </div>
                    <Badge variant="outline" className="text-slate-400 border-slate-700">
                      {c.total_ordenes} ordenes
                    </Badge>
                  </div>
                ))}
              </div>
            ) : (
              <p className="text-sm text-slate-500 text-center py-8">Sin datos de clientes</p>
            )}
          </CardContent>
        </Card>
      </div>

      <Card className="bg-slate-900/40 border-slate-800">
        <CardHeader className="pb-2 flex flex-row items-center justify-between">
          <CardTitle className="font-barlow text-lg uppercase tracking-tight text-slate-200">
            Órdenes Recientes
          </CardTitle>
          <button
            onClick={() => navigate('/ordenes')}
            className="text-xs text-amber-500 hover:text-amber-400 flex items-center gap-1 transition-colors"
            data-testid="ver-todas-ordenes-btn"
          >
            Ver todas <ArrowRight className="w-3 h-3" />
          </button>
        </CardHeader>
        <CardContent>
          {metricas.ordenes_recientes?.length > 0 ? (
            <Table>
              <TableHeader>
                <TableRow className="border-slate-800 hover:bg-transparent">
                  <TableHead className="text-xs uppercase tracking-wider text-slate-500">Orden</TableHead>
                  <TableHead className="text-xs uppercase tracking-wider text-slate-500">Cliente</TableHead>
                  <TableHead className="text-xs uppercase tracking-wider text-slate-500 hidden md:table-cell">Vehículo</TableHead>
                  <TableHead className="text-xs uppercase tracking-wider text-slate-500">Estado</TableHead>
                  <TableHead className="text-xs uppercase tracking-wider text-slate-500 text-right">Total</TableHead>
                </TableRow>
              </TableHeader>
              <TableBody>
                {metricas.ordenes_recientes.map((o) => (
                  <TableRow
                    key={o.id}
                    className="border-slate-800/50 cursor-pointer hover:bg-slate-800/40"
                    onClick={() => navigate(`/ordenes/${o.id}`)}
                    data-testid={`orden-row-${o.id}`}
                  >
                    <TableCell className="font-mono text-xs text-amber-500">{o.numero_orden}</TableCell>
                    <TableCell className="text-sm text-slate-300">{o.cliente_nombre}</TableCell>
                    <TableCell className="text-sm text-slate-400 hidden md:table-cell">{o.vehiculo_info || '-'}</TableCell>
                    <TableCell>
                      <span className={`inline-flex items-center rounded-sm border px-2 py-0.5 text-xs font-semibold ${ESTADOS[o.estado]?.clase || ''}`}>
                        {ESTADOS[o.estado]?.label || o.estado}
                      </span>
                    </TableCell>
                    <TableCell className="text-sm text-slate-300 text-right">{formatoMXN(o.total)}</TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          ) : (
            <p className="text-sm text-slate-500 text-center py-8">No hay ordenes recientes</p>
          )}
        </CardContent>
      </Card>
    </div>
  );
}
